/** The tag-category association flags — ONE catalog for every surface that shows a
 *  `Group_Tour_Tag_Category_M2M` row: the operator's default tag categories (the tour
 *  operator's Defaults tab) and a tour's own rows (backend plan
 *  `2026-10-07-console-operator-defaults-tab.md` §A1 "Flag catalog").
 *
 *  - `ALL_FLAGS` — the 19 booleans of a row, in the matrix's column order (grouped).
 *  - `FLAGS` / `FLAG_BY_KEY` — per flag: the long label (the existing console wording), the
 *    short label for a vertical column header, its group, what it does (`help`) and the
 *    rule that constrains it (`rule`, shown in the header tooltip; the rules themselves
 *    live in `rules.ts`).
 *  - `KEY_FLAGS` — the six a narrow host shows (`columns="key"`).
 *  - `NEW_ROW_DEFAULTS` — what an added row starts with (D5).
 *  - Tag counts: `min_tags` 0 = optional, `max_tags` 0 = unlimited; the dashboard writes
 *    32767 for "many", so anything ≥ 32767 reads as unlimited too.
 *
 *  The tour editor still shows its own labels, order and groups for 18 of these
 *  (`tours/record/catalog.ts` `TAGCAT_FLAG_LABELS`) — never `departure_key`, which the
 *  console tour serializer does not expose. */

export const ALL_FLAGS = [
  'checkin',
  'boarding',
  'checkin_display',
  'user_selected',
  'visible_on_bookingmgmt',
  'copy_tags_to_subbookings',
  'chat_conversation',
  'visible_in_chat',
  'location_sharing',
  'location_all_if_no_tag',
  'user_association',
  'guides_from_tags',
  'copy_tags_to_guides',
  'remove_guides_on_cleanup',
  'remove_unreferenced_guides',
  'cleanup_on_assignment',
  'departure_wide',
  'departure_key',
  'remove_upon_date_changes',
] as const;

export type Flag = (typeof ALL_FLAGS)[number];

export type FlagGroupId = 'checkin' | 'guests' | 'chat' | 'location' | 'guides' | 'lifecycle';

export interface FlagGroup {
  id: FlagGroupId;
  title: string;
  flags: readonly Flag[];
}

/** The groups, in column order; together they hold `ALL_FLAGS` exactly once, in order. */
export const FLAG_GROUPS: readonly FlagGroup[] = [
  {
    id: 'checkin',
    title: 'Check-in & boarding',
    flags: ['checkin', 'boarding', 'checkin_display'],
  },
  {
    id: 'guests',
    title: 'Guests',
    flags: ['user_selected', 'visible_on_bookingmgmt', 'copy_tags_to_subbookings'],
  },
  { id: 'chat', title: 'Chat', flags: ['chat_conversation', 'visible_in_chat'] },
  {
    id: 'location',
    title: 'Location',
    flags: ['location_sharing', 'location_all_if_no_tag', 'user_association'],
  },
  {
    id: 'guides',
    title: 'Guides',
    flags: [
      'guides_from_tags',
      'copy_tags_to_guides',
      'remove_guides_on_cleanup',
      'remove_unreferenced_guides',
    ],
  },
  {
    id: 'lifecycle',
    title: 'Lifecycle',
    flags: ['cleanup_on_assignment', 'departure_wide', 'departure_key', 'remove_upon_date_changes'],
  },
];

export interface FlagDef {
  flag: Flag;
  /** The full label (tooltips, the column header's accessible name). */
  label: string;
  /** The vertical column header ("Check-in"), and the control's name ("Check-in of MEAL"). */
  short: string;
  group: FlagGroupId;
  /** What the flag does, in a sentence. */
  help: string;
  /** What constrains it (`rules.ts`), when anything does. */
  rule?: string;
}

const def = (
  flag: Flag,
  group: FlagGroupId,
  label: string,
  short: string,
  help: string,
  rule?: string,
): FlagDef => ({
  flag,
  group,
  label,
  short,
  help,
  ...(rule ? { rule } : {}),
});

export const FLAGS: readonly FlagDef[] = [
  def(
    'checkin',
    'checkin',
    'Used for check-in (at most 3 categories)',
    'Check-in',
    'Tags in this category are used for check-in. The number is the category’s place in the check-in order.',
    'At most 3 categories; not on a row used for boarding',
  ),
  def(
    'boarding',
    'checkin',
    'Used for boarding (at most 1 category)',
    'Boarding',
    'Tags in this category are used for boarding.',
    'At most 1 category; not on a row used for check-in',
  ),
  def(
    'checkin_display',
    'checkin',
    'Shown on the check-in display',
    'Check-in display',
    'Tags in this category are shown on the check-in screen.',
  ),
  def(
    'user_selected',
    'guests',
    'Self-selectable at self-registration',
    'Self-select',
    'Guests pick a tag of this category themselves on the self-registration page.',
  ),
  def(
    'visible_on_bookingmgmt',
    'guests',
    'Visible on booking management',
    'Booking mgmt',
    'Guests see these tags on the booking-management page.',
  ),
  def(
    'copy_tags_to_subbookings',
    'guests',
    'Copy tags to sub-bookings',
    'Sub-bookings',
    'Tags in this category are copied to sub-bookings.',
  ),
  def(
    'chat_conversation',
    'chat',
    'Tags create chat conversations',
    'Chat groups',
    'Each tag in this category creates its own chat conversation.',
  ),
  def(
    'visible_in_chat',
    'chat',
    'Visible in chat',
    'In chat',
    'Tags in this category appear in chat.',
  ),
  def(
    'location_sharing',
    'location',
    'Tags define location-sharing groups',
    'Location groups',
    'Tags in this category define who shares their location with whom.',
    'Stays on while “GPS / users” is on',
  ),
  def(
    'location_all_if_no_tag',
    'location',
    'Share with everyone when the guest has no tag',
    'All if no tag',
    'A guest without a tag of this category sees everyone in the category’s location sharing.',
  ),
  def(
    'user_association',
    'location',
    'Tags link to GPS devices / users',
    'GPS / users',
    'Tags in this category link to GPS devices / users — location sharing without chat.',
    'Turning it on turns “Location groups” on and “Guides” off',
  ),
  def(
    'guides_from_tags',
    'guides',
    'Tags link to guides / staff',
    'Guides',
    'Tags in this category link to guides / staff, who are added for location sharing and chat.',
    'Turning it on turns “GPS / users” off; turning it off turns “Remove unreferenced” off',
  ),
  def(
    'copy_tags_to_guides',
    'guides',
    'Copy tags to guides',
    'Copy to guides',
    'Guests’ tags in this category are copied to the guides added from (other) tags.',
  ),
  def(
    'remove_guides_on_cleanup',
    'guides',
    'Remove guides on clean-up',
    'Remove on clean-up',
    'When a tag of this category is removed, the guides it added are removed from the guests.',
    'Needs “Clean-up”',
  ),
  def(
    'remove_unreferenced_guides',
    'guides',
    'Remove guides no tag refers to',
    'Remove unreferenced',
    'Guides added from tags are removed once no booking in the departure refers to their tag.',
    'Needs “Guides”',
  ),
  def(
    'cleanup_on_assignment',
    'lifecycle',
    'Clean up on each assignment',
    'Clean-up',
    'Tags of this category are removed on each assignment.',
    'Turning it off turns “Remove on clean-up” off',
  ),
  def(
    'departure_wide',
    'lifecycle',
    'Departure-wide (same tags for everyone on a departure)',
    'Departure-wide',
    'Tags in this category stay the same for every guest on the same departure.',
  ),
  def(
    'departure_key',
    'lifecycle',
    'Tags identify the departure instance (advanced)',
    'Departure key',
    'Tags identify the departure instance (advanced): guests on the same tour and start time with different tags of this category are separate departures, and departure-wide tags and guides are kept apart between them.',
  ),
  def(
    'remove_upon_date_changes',
    'lifecycle',
    'Remove tags when the date or time changes',
    'Remove on date change',
    'Tags of this category are removed when an import or integration changes the date or departure time.',
  ),
];

export const FLAG_BY_KEY: Readonly<Record<Flag, FlagDef>> = Object.fromEntries(
  FLAGS.map((f) => [f.flag, f]),
) as Record<Flag, FlagDef>;

export const FLAG_GROUP_BY_ID: Readonly<Record<FlagGroupId, FlagGroup>> = Object.fromEntries(
  FLAG_GROUPS.map((g) => [g.id, g]),
) as Record<FlagGroupId, FlagGroup>;

/** The six flags a narrow host shows (the tour table's columns). */
export const KEY_FLAGS: readonly Flag[] = [
  'checkin',
  'boarding',
  'chat_conversation',
  'user_selected',
  'location_sharing',
  'guides_from_tags',
];

export const isFlag = (s: string): s is Flag => (ALL_FLAGS as readonly string[]).includes(s);

/** Every flag off. */
export const NO_FLAGS: Readonly<Record<Flag, boolean>> = Object.fromEntries(
  ALL_FLAGS.map((f) => [f, false]),
) as Record<Flag, boolean>;

/** An added row (D5): what dashboard-created defaults get today — copied to sub-bookings
 *  and visible on booking management, everything else off; any number of tags, none
 *  required. */
export const NEW_ROW_DEFAULTS: Readonly<{
  min_tags: number;
  max_tags: number;
  flags: Readonly<Record<Flag, boolean>>;
}> = {
  min_tags: 0,
  max_tags: 0,
  flags: { ...NO_FLAGS, copy_tags_to_subbookings: true, visible_on_bookingmgmt: true },
};

// ---------------------------------------------------------------------------
// Tag counts
// ---------------------------------------------------------------------------

/** The dashboard's "many" (the column's PositiveSmallIntegerField maximum). */
export const MAX_TAGS_UNLIMITED = 32767;

/** `max_tags` 0 (the model's "unlimited") or the dashboard's 32767. */
export const isUnlimited = (max: number): boolean => max === 0 || max >= MAX_TAGS_UNLIMITED;

/** "0–1", "1–1", "0–∞", "1–∞", "2–5". */
export function formatCounts(min: number, max: number): string {
  return `${min}–${isUnlimited(max) ? '∞' : max}`;
}

/** The counts in words ("optional, at most 1 tag per guest"). */
export function describeCounts(min: number, max: number): string {
  const lower = min === 0 ? 'optional' : `at least ${min}`;
  const upper = isUnlimited(max)
    ? 'no maximum'
    : `at most ${max} ${max === 1 ? 'tag' : 'tags'} per guest`;
  return `${lower}, ${upper}`;
}

export interface CountPreset {
  id: 'optional-one' | 'required-one' | 'optional-many' | 'required-many';
  label: string;
  min: number;
  /** null: unlimited (the row keeps a stored 32767, else 0). */
  max: number | null;
}

/** The four common shapes of a category's tag counts. */
export const COUNT_PRESETS: readonly CountPreset[] = [
  { id: 'optional-one', label: 'Optional · one', min: 0, max: 1 },
  { id: 'required-one', label: 'Required · one', min: 1, max: 1 },
  { id: 'optional-many', label: 'Optional · many', min: 0, max: null },
  { id: 'required-many', label: 'Required · many', min: 1, max: null },
];

/** The counts a preset gives a row: "many" keeps a stored unlimited value (32767 stays
 *  32767), else it is 0. */
export function applyPreset(
  preset: CountPreset,
  current: { max_tags: number },
): { min_tags: number; max_tags: number } {
  const max = preset.max ?? (isUnlimited(current.max_tags) ? current.max_tags : 0);
  return { min_tags: preset.min, max_tags: max };
}

/** The preset the counts match (null: none). */
export function presetOf(min: number, max: number): CountPreset | null {
  return (
    COUNT_PRESETS.find(
      (p) => p.min === min && (p.max === null ? isUnlimited(max) : p.max === max),
    ) ?? null
  );
}
